import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /** @type {{ soldOut: boolean, href: string, label: string } | null} */
  #sizeRequest = null;

  /** Maison: en mobile la barra se ve desde que carga la página hasta que aparece el botón original */
  #mobileQuery = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.addEventListener('click', this.#handleSizeSheetClick, { signal });
    document.addEventListener('maison:size-request', this.#handleSizeRequest, { signal });
    document.addEventListener('keydown', this.#handleSizeSheetKeydown, { signal });
    this.#mobileQuery.addEventListener('change', this.#refreshVisibility, { signal });

    this.#getInitialQuantity();
    this.#updateSizeValue();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = /** @type {HTMLElement | null} */ (productForm.closest('.buy-buttons-block'));
    if (!buyButtonsBlock) return;
    this.#buyButtonsBlock = buyButtonsBlock;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Desktop: only after the buy buttons were scrolled past. Mobile: also before reaching them.
      if (!entry.isIntersecting && !this.#isStuck) {
        if (this.#shouldShowFor(entry.target.getBoundingClientRect())) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (this.#shouldShowFor(rect)) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Whether the sticky bar should show for the given position of the original buy buttons
   * @param {DOMRect} rect
   * @returns {boolean}
   */
  #shouldShowFor(rect) {
    const above = rect.bottom < 0 || rect.top < 0;
    if (!this.#mobileQuery.matches) return above;
    const below = rect.top >= window.innerHeight;
    return above || below;
  }

  /** Re-evaluates the bar when switching between mobile and desktop widths */
  #refreshVisibility = () => {
    if (!this.#buyButtonsBlock || this.#hiddenByBottom) return;
    const rect = this.#buyButtonsBlock.getBoundingClientRect();
    const inView = rect.bottom > 0 && rect.top < window.innerHeight;
    if (!inView && this.#shouldShowFor(rect)) this.#showStickyBar();
    else this.#hideStickyBar();
    if (!this.#mobileQuery.matches) this.#closeSizeDrop();
  };

  // Maison: desplegable de talles arriba de la barra (mobile)
  /** @returns {HTMLElement | null} */
  #getVariantPicker() {
    const section = this.closest('.shopify-section') ?? document;
    return section.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
  }

  /**
   * Reads every option of the product from the page's variant picker
   * (buttons, dropdowns or a single fixed value).
   * @returns {{ name: string, values: { text: string, selected: boolean, off: boolean, control: HTMLInputElement | HTMLSelectElement | null, value: string }[] }[]}
   */
  #getOptions() {
    const picker = this.#getVariantPicker();
    if (!picker) return [];
    const clean = (/** @type {string | null | undefined} */ text) => (text ?? '').replace(/\s+/g, ' ').trim();

    return Array.from(picker.querySelectorAll('.variant-option')).flatMap((el) => {
      // La condición se muestra como dato (fila fija), sus botones ocultos se ignoran
      if (el.matches('fieldset[data-maison-cond]')) return [];
      if (el.matches('fieldset')) {
        const name = clean(el.querySelector('legend')?.childNodes[0]?.textContent);
        const values = Array.from(el.querySelectorAll('input[type="radio"]')).map((node) => {
          const input = /** @type {HTMLInputElement} */ (node);
          const text = clean(input.closest('label')?.querySelector('.variant-option__button-label__text')?.textContent) || input.value;
          return { text, selected: input.checked, off: input.getAttribute('aria-disabled') === 'true', control: input, value: input.value };
        });
        return [{ name, values }];
      }
      const select = /** @type {HTMLSelectElement | null} */ (el.querySelector('select'));
      if (select) {
        const name = clean(el.querySelector('label')?.textContent);
        const values = Array.from(select.options).map((option) => ({
          text: clean(option.textContent),
          selected: option.selected,
          off: option.dataset.optionAvailable === 'false',
          control: select,
          value: option.value,
        }));
        return [{ name, values }];
      }
      const single = el.querySelector('.variant-option__swatch-value');
      if (single) {
        const name = clean(el.querySelector('.variant-option__single-label')?.textContent);
        return [{ name, values: [{ text: clean(single.textContent), selected: true, off: false, control: null, value: '' }] }];
      }
      return [];
    });
  }

  /** Shows the selected size in the bar; with no choice to make, the button stays static */
  #updateSizeValue() {
    const chip = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-msize-open]'));
    if (!chip) return;
    const options = this.#getOptions();
    if (!options.length) return;

    const first = options[0];
    const selected = first?.values.find((v) => v.selected) ?? first?.values[0];
    const label = chip.querySelector('.sticky-add-to-cart__size-label');
    const valueEl = chip.querySelector('[data-msize-value]');
    if (label && first?.name) label.textContent = first.name;
    if (valueEl && selected) valueEl.textContent = selected.text;

    const hasChoice = options.some((option) => option.values.length > 1);
    chip.classList.toggle('is-static', !hasChoice);
    chip.setAttribute('aria-disabled', hasChoice ? 'false' : 'true');
    chip.setAttribute('aria-expanded', this.#isDropOpen() ? 'true' : 'false');
  }

  /** Builds the option buttons inside the dropdown */
  #renderSizeDrop() {
    const container = this.querySelector('[data-msize-options]');
    if (!container) return;

    const groups = this.#getOptions().map((option, optionIndex) => {
      const group = document.createElement('div');
      group.className = 'msize-drop__group';

      const legend = document.createElement('p');
      legend.className = 'msize-drop__legend';
      legend.textContent = option.name;

      // Opción sin elección (por ejemplo la condición): se muestra como dato
      if (option.values.every((v) => !v.control)) {
        group.className = 'msize-drop__info';
        const value = document.createElement('span');
        value.className = 'msize-drop__info-value';
        value.textContent = option.values.find((v) => v.selected)?.text ?? option.values[0]?.text ?? '';
        group.append(legend, value);
        return group;
      }

      const grid = document.createElement('div');
      grid.className = 'msize-drop__grid';
      if (option.values.some((v) => v.text.length > 7)) grid.classList.add('msize-drop__grid--wide');

      option.values.forEach((value, valueIndex) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'msize-drop__opt';
        button.textContent = value.text;
        button.dataset.optionIndex = String(optionIndex);
        button.dataset.valueIndex = String(valueIndex);
        button.setAttribute('aria-pressed', value.selected ? 'true' : 'false');
        if (!value.control) button.setAttribute('aria-disabled', 'true');
        if (value.off) button.classList.add('is-off');
        grid.append(button);
      });

      group.append(legend, grid);
      return group;
    });

    container.replaceChildren(...groups);

    const guideButton = /** @type {HTMLElement | null} */ (this.querySelector('[data-msize-guide]'));
    if (guideButton) guideButton.hidden = !this.#getSizeGuideButton();
  }

  /** @returns {HTMLElement | null} */
  #getSizeGuideButton() {
    const section = this.closest('.shopify-section') ?? document;
    return section.querySelector('.maison-size-guide__open');
  }

  /** @returns {HTMLElement | null} */
  #getSizeDrop() {
    return this.querySelector('[data-msize-drop]');
  }

  #isDropOpen() {
    return Boolean(this.#getSizeDrop()?.classList.contains('is-open'));
  }

  #openSizeDrop() {
    const drop = this.#getSizeDrop();
    const chip = this.querySelector('[data-msize-open]');
    if (!drop || !chip || chip.classList.contains('is-static')) return;
    this.#renderSizeDrop();
    this.style.setProperty('--msize-bar-h', `${this.refs.stickyBar.offsetHeight}px`);
    drop.hidden = false;
    chip.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      drop.classList.add('is-open');
      const current = /** @type {HTMLElement | null} */ (drop.querySelector('.msize-drop__opt[aria-pressed="true"]'));
      current?.focus({ preventScroll: true });
    });
    document.addEventListener('click', this.#handleOutsideClick, { capture: true, signal: this.#abortController.signal });
  }

  /** @param {boolean} [returnFocus] */
  #closeSizeDrop(returnFocus = false) {
    const drop = this.#getSizeDrop();
    if (!drop || drop.hidden) return;
    drop.classList.remove('is-open');
    const chip = /** @type {HTMLElement | null} */ (this.querySelector('[data-msize-open]'));
    chip?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.#handleOutsideClick, { capture: true });
    setTimeout(() => {
      if (!drop.classList.contains('is-open')) drop.hidden = true;
    }, 200);
    if (returnFocus) chip?.focus({ preventScroll: true });
  }

  /** @param {MouseEvent} event */
  #handleOutsideClick = (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (this.#getSizeDrop()?.contains(target) || this.querySelector('[data-msize-open]')?.contains(target)) return;
    this.#closeSizeDrop();
  };

  /** @param {MouseEvent} event */
  #handleSizeSheetClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-msize-open]')) {
      if (this.#isDropOpen()) this.#closeSizeDrop();
      else this.#openSizeDrop();
      return;
    }

    const button = /** @type {HTMLElement | null} */ (target.closest('.msize-drop__opt'));
    if (button) {
      const option = this.#getOptions()[Number(button.dataset.optionIndex)];
      const value = option?.values[Number(button.dataset.valueIndex)];
      if (!value?.control) return;
      if (!value.selected) {
        button.parentElement?.querySelectorAll('.msize-drop__opt').forEach((el) => el.setAttribute('aria-pressed', 'false'));
        button.setAttribute('aria-pressed', 'true');
        const valueEl = this.querySelector('[data-msize-value]');
        if (Number(button.dataset.optionIndex) === 0 && valueEl) valueEl.textContent = value.text;
        if (value.control instanceof HTMLSelectElement) {
          value.control.value = value.value;
          value.control.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          value.control.click();
        }
      }
      // Con un solo grupo para elegir (solo talle), el desplegable se cierra al elegir
      const choiceGroups = this.#getOptions().filter((o) => o.values.length > 1).length;
      if (choiceGroups <= 1) this.#closeSizeDrop();
      return;
    }

    if (target.closest('[data-msize-guide]')) {
      this.#closeSizeDrop();
      this.#getSizeGuideButton()?.click();
    }
  };

  /** @param {KeyboardEvent} event */
  #handleSizeSheetKeydown = (event) => {
    if (event.key === 'Escape') this.#closeSizeDrop(true);
  };

  /**
   * Maison: talle sin stock → el botón de la barra pasa a "Encargá tu talle" y abre WhatsApp
   * @param {Event} event
   */
  #handleSizeRequest = (event) => {
    const section = this.closest('.shopify-section');
    if (section && event.target instanceof Node && !section.contains(event.target)) return;
    this.#sizeRequest = /** @type {CustomEvent} */ (event).detail;
    this.#applySizeRequest();
  };

  #applySizeRequest() {
    const button = this.refs.addToCartButton;
    const request = this.#sizeRequest;
    if (!button || !request) return;
    const textEl = button.querySelector('.add-to-cart-text__content > span');
    if (request.soldOut) {
      if (textEl && button.dataset.request !== 'true') button.dataset.cartLabel = textEl.textContent ?? '';
      button.removeAttribute('disabled');
      button.dataset.request = 'true';
      if (textEl && request.label) textEl.textContent = request.label;
    } else if (button.dataset.request === 'true') {
      delete button.dataset.request;
      if (textEl && button.dataset.cartLabel) textEl.textContent = button.dataset.cartLabel;
    }
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.refs.addToCartButton.dataset.request === 'true' && this.#sizeRequest?.href) {
      window.open(this.#sizeRequest.href, '_blank', 'noopener');
      return;
    }
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    this.#applySizeRequest();

    // Maison: talle elegido en la barra y opciones del panel, una vez que el selector se actualizó
    requestAnimationFrame(() => {
      this.#updateSizeValue();
      if (this.#isDropOpen()) this.#renderSizeDrop();
    });
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    this.#closeSizeDrop();
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
